import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, computed, viewChild, ElementRef, AfterContentChecked, signal, input, effect, OnDestroy, Output, EventEmitter, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { ZIndexUtils } from '@js/primeng17/utils';
import { PrimeNGConfig } from '@js/primeng17/api';
import { UiTranslateService } from '../../helper/lang';

export enum InlineAlertSeverity {
  error = 'error',
  warning = 'warning',
  success = 'success',
  info = 'info',
}

export enum Position {
  none = 'none',
  top = 'top',
  left = 'left',
  bottom = 'bottom',
  right = 'right'
}

@Component({
    selector: 'ig-inline-alert',
    templateUrl: './inline-alert.component.html',
    styleUrls: ['./inline-alert.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InlineAlertComponent implements AfterContentChecked, OnInit, OnDestroy {
  /**
   * Defines the severity of the alert (defaults to "warning")
   */
  @Input() severity: InlineAlertSeverity | string = InlineAlertSeverity.warning;

  /**
   * Whether there should be a "close" icon
   */
  @Input() dissmissable: boolean = false;

  /**
   * Should this alert be displayed as a bubble (border radius and shadow and also an arrow if the bubblePositionFromRelated is set)
   */
  asBubble = input<boolean>();

  /**
   * The position the bubble is displayed from its related element (will influence the bubble cursor)
   */
  @Input() bubblePositionFromRelated: Position | string = Position.none;

  /**
   * hide the icon
   */
  @Input() hideIcon = false;

  /**
   * icon class for custom icon
   */
  @Input() customIcon?: string;

  /**
   * large mode
   */
  @Input() large = false;

  /**
   * light style
   */
  @Input() lightStyle = false;

  @Input() ariaLabel?: string;
  @Input() ariaDescription?: string;


  /**
   * @ignore
   */
  @Input() storybookFakeContent?: string;

  /**
   * @ignore
   */
  @Output() onDismiss = new EventEmitter<any>();

  containerEl = viewChild<ElementRef>('container');
  contentEl = viewChild<ElementRef>('contentContainer');

  /**
   * @ignore
   */
  readonly severityClasses = {
    error: 'ig-inline-alert-severity-error',
    warning: 'ig-inline-alert-severity-warning',
    success: 'ig-inline-alert-severity-success',
    info: 'ig-inline-alert-severity-info',
  };

  /**
   * @ignore
   */
  readonly positionClasses = {
    none: '',
    top: 'ig-inline-alert-position-top',
    left: 'ig-inline-alert-position-left',
    bottom: 'ig-inline-alert-position-bottom',
    right: 'ig-inline-alert-position-right',
  };

  /**
   * @ignore
   */
  closed = false;

  /**
   * @ignore
   */
  style: any = {};

  lastContent = signal<string>('');

  marginTop = computed(() => {
    // compute margin top in bubble mode, depending on the content height to make it vertically centered
    const lastContent = this.lastContent(); // no not remove! just to let the effect run on content check
    if (this.asBubble() && (this.bubblePositionFromRelated === Position.left || this.bubblePositionFromRelated === Position.right)) {
      return Math.round(this.containerEl()?.nativeElement?.getBoundingClientRect().height / -2) + 'px';
    }
    return '';
  })

  movedToBody = false;
  placeholderEl?: HTMLElement;
  contentHidden = false;
  contentHiddenTimeout?: number;


  constructor(private readonly ref: ChangeDetectorRef, private config: PrimeNGConfig, private translate: UiTranslateService) {
    effect(() => {
      // this are calculations done in bubble mode, will move the alert content up to the next p-scrollpanel (or document.body if no scrollpanel found)
      // to let the alert be positioned absolute and let it cover other elements
      // also to position calculations

      const asBubble = this.asBubble();
      const container = this.containerEl();
      const lastContent = this.lastContent(); // do not remove! just to let the effect run on content check

      if (container?.nativeElement) {
        if (asBubble) {
          let newContainer: HTMLElement | undefined;

          // check if one of our parent has the '.p-scrollpanel-content'
          let parent = container.nativeElement.parentElement as HTMLElement;
          while (parent && newContainer === undefined) {
            if (parent.classList.contains('p-scrollpanel-content')) {
              newContainer = parent;
            }
            parent = parent.parentElement as HTMLElement;
          }
          if (newContainer === undefined) {
            // no scroll container found? use the body
            newContainer = document.body;
          }


          if (!this.movedToBody) {
            // create a placeholder and set it after our container...
            if (!this.placeholderEl) {
              const rectContainer = (container.nativeElement as HTMLElement).getBoundingClientRect();
              const rectNewContainer = newContainer.getBoundingClientRect();
              container.nativeElement.style.top = ((rectContainer.top + newContainer.scrollTop + rectContainer.height / 2) - rectNewContainer.top) + 'px';
              container.nativeElement.style.left = (rectContainer.left - rectNewContainer.left) + 'px';

              this.placeholderEl = document.createElement('div');
              (container.nativeElement as HTMLElement).parentElement?.appendChild(this.placeholderEl);
            }

            //... then move the container as direct body child so we can apply z-index and this stuff
            newContainer.appendChild(container.nativeElement);
            this.movedToBody = true;

          } else {
            // if already moved to body, correct positions
            if (this.placeholderEl) {
              const rectContainer = this.placeholderEl.getBoundingClientRect();
              const rectNewContainer = newContainer.getBoundingClientRect();
              container.nativeElement.style.top = ((rectContainer.top + newContainer.scrollTop + rectContainer.height / 2) - rectNewContainer.top) + 'px';
              container.nativeElement.style.left = (rectContainer.left - rectNewContainer.left) + 'px';

            }
          }

        } else {
          this.#moveBackToOrigin();

        }
      }
    })
  }

  ngOnInit(): void {
    if (this.ariaRole === 'alert') {
      this.contentHidden = true;
    }
  }

  ngAfterContentChecked(): void {

    if (this.ariaRole === 'alert') {
      if (!this.contentHiddenTimeout) {
        this.contentHiddenTimeout = window.setTimeout(() => {
          this.contentHidden = false;
          this.ref.markForCheck();
        }, 200)
      }
    }


    if (this.contentEl()?.nativeElement?.innerHTML !== this.lastContent()) {
      // content updated
      this.lastContent.set(this.contentEl()?.nativeElement?.innerHTML);

      if (this.containerEl()?.nativeElement && this.asBubble()) {
        ZIndexUtils.set('ig-inline-alert-bubble', this.containerEl()?.nativeElement, this.config.zIndex.tooltip);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.contentHiddenTimeout) {
      window.clearTimeout(this.contentHiddenTimeout);
    }
    this.#moveBackToOrigin();
  }

  /**
   * closes the alert
   */
  close() {
    this.closed = true;
    this.#moveBackToOrigin();
    this.onDismiss.emit();
    this.ref.markForCheck();
  }

  get positionClass() {
    return _.get(this.positionClasses, this.bubblePositionFromRelated, '');
  }

  get severityClass() {
    return _.get(this.severityClasses, this.severity, 'info');
  }

  get severityText() {
    return this.translate.instant('SEVERITIES.' + this.severity);
  }

  /**
   * if the alert container was moved to body or scroller in the DOM, move it back to where it belongs (on close/destroey)
   */
  #moveBackToOrigin() {
    const container = this.containerEl();
    if (this.movedToBody && this.placeholderEl && container) {
      this.placeholderEl.parentElement?.appendChild(container.nativeElement);
      container.nativeElement.style.top = '';
      container.nativeElement.style.left = '';
      this.movedToBody = false;
    }
  }

  get role() {
    if (this.ariaRole) {
      return this.ariaRole;
    }
    if (this.severity === InlineAlertSeverity.error) {
      return 'alert';
    }

    return 'region';
  }

  get ariaRole() {
    if (this.severity === InlineAlertSeverity.error) {
      return 'alert';
    }
    return '';
  }

}
